import React, { useState } from 'react';
import { Grid, Button, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

import TimeSlot from './TimeSlot';
import { WorkHours } from '../types';

/**
 * Props for the DaySchedule component
 */
interface DayScheduleProps {
    label: string;
    isChecked?: boolean;
    getSource: (source: string) => string;
}

/**
 * A component that displays a day's schedule with a checkbox and time slots
 * Allows adding and removing time slots dynamically
 */
const DaySchedule: React.FC<DayScheduleProps> = ({ label, getSource, isChecked }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const { control, getValues, setValue, trigger } = useFormContext();

    const pathWithDot = getSource('');
    const basePath = pathWithDot?.endsWith('.') ? pathWithDot.slice(0, -1) : pathWithDot;

    if (!basePath) return null;

    const { fields, append, remove, replace } = useFieldArray({ control, name: basePath });

    // helper to ensure at most one empty slot exists (keep non-empty entries + one empty)
    const normalizeSlots = (arr: WorkHours[]) => {
        const list = Array.isArray(arr) ? arr : [];
        const nonEmpty = list.filter((s) => s && (s.from || s.to));
        if (nonEmpty.length === 0) return [{ from: '', to: '' }];
        return [...nonEmpty, { from: '', to: '' }];
    };

    const checkSlots = () => {
        try {
            const raw = getValues(basePath) || [];
            const normalized = normalizeSlots(raw);
            // only update if normalization changed the array
            if (JSON.stringify(normalized) !== JSON.stringify(raw)) {
                replace(normalized);
                setValue(basePath, normalized, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true
                });
            }
            void trigger(basePath);
        } catch (e) {
            // ignore
        }
    };

    if (!isInitialized) {
        checkSlots();
        setIsInitialized(true);
    }

    const addTimeSlot = () => {
        const raw = getValues(basePath) || [];
        const hasEmpty = raw.some((s: WorkHours) => !s?.from && !s?.to);
        if (hasEmpty) return;
        append({ from: '', to: '', isNew: true });
        void trigger(basePath);
    };

    const removeTimeSlot = (index: number) => {
        const raw = getValues(basePath) || [];
        let newArr = raw.filter((_: WorkHours, i: number) => i !== index);
        // normalize to keep only non-empty + one empty
        newArr = normalizeSlots(newArr);
        if (newArr.length <= 1) {
            replace(newArr);
            setValue(basePath, newArr, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        } else {
            // if more than 1, use field array remove which handles unregistering
            remove(index);
        }
        void trigger(basePath);
    };

    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <FormControlLabel control={<Checkbox />} label={label} checked={isChecked} />
            </Grid>

            {fields.map((field, index) => {
                let fromVal: string | undefined = undefined;
                let toVal: string | undefined = undefined;
                try {
                    fromVal = getValues(`${basePath}.${index}.from`);
                    toVal = getValues(`${basePath}.${index}.to`);
                } catch (e) {
                    fromVal = (field as WorkHours).from;
                    toVal = (field as WorkHours).to;
                }
                const hasValue = !!fromVal || !!toVal;

                return (
                    <Grid
                        key={(field as any).id || index}
                        item
                        container
                        justifyContent="space-between"
                        alignItems="center">
                        <Grid item>
                            <Typography>Години роботи:</Typography>
                        </Grid>
                        <Grid item>
                            <TimeSlot
                                index={index}
                                getSource={(source) => `${basePath}.${index}.${source}`}
                                onSlotChange={() => checkSlots()}
                            />
                        </Grid>
                        <Grid item>
                            {hasValue ? (
                                <Button
                                    onClick={() => removeTimeSlot(index)}
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}>
                                    Видалити
                                </Button>
                            ) : (
                                <Button onClick={addTimeSlot} variant="outlined" startIcon={<AddCircleOutlineIcon />}>
                                    Інтервал
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default DaySchedule;
